import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiArrowUpRight, FiCheck } from 'react-icons/fi';
import PublicPage from '../components/public/PublicPage';
import usePageTitle from '../hooks/usePageTitle';
import './LandingPage.css';
import './PublicPages.css';
import './PricingPage.css';

// The commercial model from the product plan: what each offer is for and how it is charged.
// No rand amounts are shown because none are fixed yet — see frontend/.env.example / SITE config
// for the pattern this app follows: nothing is invented, only confirmed facts are shown.
const PLANS = [
  {
    name: 'Rental applicants',
    audience: 'Applying for a property',
    price: 'Free to apply',
    text: 'Complete your application through the link, QR code or invitation from your landlord or agent. Applying, correcting your details and exercising your privacy rights never costs anything.',
    cta: null
  },
  {
    name: 'Single report',
    audience: 'Private landlords',
    price: 'Let’s talk',
    text: 'A fixed price per completed report, plus any provider costs — such as identity or credit checks — disclosed to you before the check runs.',
    cta: { to: '/contact', label: 'Ask about pricing' },
    highlight: true
  },
  {
    name: 'Professional plan',
    audience: 'Agents & small property managers',
    price: 'Let’s talk',
    text: 'A monthly fee with a set number of reports included, and clear per-report pricing for anything you need beyond that.',
    cta: { to: '/contact', label: 'Ask about pricing' }
  }
];

const MORE_PLANS = [
  {
    name: 'Portfolio plan',
    audience: 'Large property managers & student accommodation',
    text: 'Contracted pricing for report volume, additional users and integration with the systems you already use.'
  },
  {
    name: 'API access',
    audience: 'Rental platforms & marketplaces',
    text: 'A platform fee plus usage, for teams building verification directly into their own product.'
  }
];

const PRINCIPLES = [
  { title: 'Price before you commit', text: 'You see the total price and the checks it includes before you purchase a report — never after.' },
  { title: 'Free for applicants', text: 'Applicants are never charged to apply, to correct inaccurate information, or to exercise a privacy right.' },
  { title: 'Provider costs disclosed', text: 'Where a check uses an external provider, such as a credit bureau, that cost is shown separately from our fee.' },
  { title: 'No reward for over-checking', text: 'Our pricing does not encourage running excessive or repeated checks on the same applicant.' }
];

const PricingPage = () => {
  usePageTitle('Pricing', 'Applicants apply for free. Landlords, agents and property managers: contact us for current verification pricing and plans.');

  return (
    <PublicPage className="landing pricing-page">
      <section className="pub-hero">
        <div className="public-container pub-narrow">
          <p className="pub-eyebrow">Pricing</p>
          <h1>A clear starting point</h1>
          <p className="pub-lead">
            Tell us about your properties and the checks you need. We’ll help you understand the available options and costs before you commit to anything.
          </p>
        </div>
      </section>

      <section className="landing-section">
        <div className="public-container landing-pricing">
          {PLANS.map((plan) => (
            <article className={`landing-card pricing-card${plan.highlight ? ' pricing-card--highlight' : ''}`} key={plan.name}>
              {plan.highlight && <span className="pricing-card__badge">Most common</span>}
              <p className="pricing-card__audience">{plan.audience}</p>
              <h3>{plan.name}</h3>
              <p className="landing-price">{plan.price}</p>
              <p>{plan.text}</p>
              {plan.cta && (
                <Link to={plan.cta.to} className="public-btn public-btn--primary">
                  {plan.cta.label} <FiArrowRight />
                </Link>
              )}
            </article>
          ))}
        </div>
        <p className="landing-more public-container">Questions first? See the <Link to="/faq">FAQ</Link>.</p>
      </section>

      <section className="landing-section landing-section--alt">
        <div className="public-container">
          <p className="pub-eyebrow">Larger portfolio, or building on TenantVerify?</p>
          <h2>Plans for bigger teams</h2>
          <div className="pricing-more">
            {MORE_PLANS.map((plan) => (
              <div className="pricing-more__item" key={plan.name}>
                <h3>{plan.name}</h3>
                <p className="pricing-card__audience">{plan.audience}</p>
                <p>{plan.text}</p>
              </div>
            ))}
          </div>
          <div className="pub-actions">
            <Link to="/contact" className="public-btn public-btn--primary">Talk to us about your portfolio <FiArrowUpRight /></Link>
          </div>
        </div>
      </section>

      <section className="pub-section">
        <div className="public-container">
          <p className="pub-eyebrow">How we price</p>
          <h2>Pricing you can rely on</h2>
          <div className="pub-grid pub-grid--3">
            {PRINCIPLES.map((item) => (
              <article className="pub-card pricing-principle" key={item.title}>
                <FiCheck aria-hidden="true" />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pub-cta">
        <div className="public-container pub-narrow">
          <p className="pub-eyebrow">Ready to talk pricing?</p>
          <h2>Let’s find the right plan for your properties.</h2>
          <p>Tell us how many properties you manage and the checks you need, and we’ll come back with the options and costs.</p>
          <div className="pub-actions pub-actions--center">
            <Link to="/contact" className="public-btn public-btn--light">Contact us <FiArrowUpRight /></Link>
            <Link to="/faq" className="pub-cta__link">Read the FAQ</Link>
          </div>
        </div>
      </section>
    </PublicPage>
  );
};

export default PricingPage;
